import React from 'react'
import styled from 'styled-components'
import Calendar from '@material-ui/icons/CalendarTodayOutlined'
import {spacing} from 'lib/ui/theme'
import {
  CodeHolder,
  Description,
  WidgetContainer,
  WidgetEnabledSwitch,
  WidgetHeader,
  WidgetHolder,
  WidgetTitle,
  HeaderIcon,
  HeaderLeft,
  HeaderRight,
  HeaderText,
  Preview,
  SubTitle,
  Title,
} from 'organization/Event/WidgetConfig/RedirectWidget'
import {Typography} from 'lib/ui/typography'
import {
  CalendarEventType,
  CalendarStyles,
  CalendarTemplate,
  Widget,
  WidgetCalendarProviders,
} from 'Event/Widget/template'
import {
  onChangeCheckedHandler,
  onChangeDate,
  onUnknownChangeHandler,
} from 'lib/dom'
import CopyButton from 'organization/Event/WidgetConfig/CopyButton'
import Checkbox from 'lib/ui/Checkbox'
import Select from 'lib/ui/Select'
import Option from 'lib/ui/Select/Option'
import TextField from 'lib/ui/TextField'
import LocalizedDateTimePicker from 'lib/LocalizedDateTimePicker'
import LocalizedDatePicker from 'lib/LocalizedDatePicker'
import {useEvent} from 'Event/EventProvider'
import CalendarStylesConfig from 'organization/Event/WidgetConfig/CalendarStylesConfig'
import {useWidgetCode} from 'organization/Event/WidgetConfig/generate-widget-code'
import TextArea from 'lib/ui/TextField/TextArea'
import InputLabel from 'Event/Marketplace/lib/ui/InputLabel'

export default function CalendarWidget(props: {
  calendar: CalendarTemplate
  onChange: (updatedFields: Partial<CalendarTemplate>) => void
  widget: Widget
}) {
  const {calendar, onChange, widget} = props

  const setActive = (isActive: boolean) => {
    onChange({enabled: isActive})
  }

  return (
    <WidgetHolder>
      <WidgetHeader>
        <HeaderLeft>
          <HeaderIcon>
            <Calendar />
          </HeaderIcon>
          <HeaderText>
            <Title isActive={calendar.enabled}>Add to Calendar</Title>
            <Description isActive={calendar.enabled}>
              Let attendees add your event to their calendar
            </Description>
          </HeaderText>
        </HeaderLeft>
        <HeaderRight>
          <WidgetEnabledSwitch
            checked={calendar.enabled}
            onChange={onChangeCheckedHandler(setActive)}
            aria-label="toggle visible widget calendar"
          />
        </HeaderRight>
      </WidgetHeader>

      <Config calendar={calendar} onChange={onChange} widget={widget} />
    </WidgetHolder>
  )
}

function Config(props: {
  calendar: CalendarTemplate
  onChange: (updatedFields: Partial<CalendarTemplate>) => void
  widget: Widget
}) {
  const {calendar, onChange, widget} = props

  const {event} = useEvent()

  const handleButtonChange = (updatedStyles: CalendarStyles) => {
    onChange({styles: updatedStyles})
  }

  const handleLabelChange = (label: string) => {
    onChange({label})
  }

  const handleUpdateProviders = (
    provider: WidgetCalendarProviders,
    isChecked: boolean,
  ) => {
    const updatedProviders = isChecked
      ? [...(calendar.providers || []), provider]
      : (calendar.providers || []).filter((p) => p !== provider)

    onChange({providers: updatedProviders})
  }

  const isProviderChecked = (provider: WidgetCalendarProviders) => {
    return calendar.providers?.includes(provider) || false
  }

  const handleEventTypeChange = (eventType: CalendarEventType) => {
    onChange({eventType})
  }

  const handleCalendarEventStartChange = (date: string) => {
    onChange({eventStart: date})
  }

  const handleCalendarEventEndChange = (date: string) => {
    onChange({eventEnd: date})
  }

  const handleDescriptionChange = (description: string) => {
    onChange({description})
  }

  const handleTitleChange = (title: string) => {
    onChange({title})
  }

  const {widgetCode, regenerate} = useWidgetCode('calendar', widget)

  if (!calendar.enabled) {
    return null
  }

  return (
    <>
      <Preview>
        <TextField
          label="Label"
          value={calendar.label}
          fullWidth
          variant="outlined"
          onChange={handleLabelChange}
        />

        <TextField
          label="Event title"
          value={
            calendar.title && calendar.title !== ''
              ? calendar.title
              : event.name
          }
          fullWidth
          variant="outlined"
          onChange={handleTitleChange}
        />

        <InputLabel>Event description</InputLabel>

        <TextArea
          rows={3}
          label="Event Description"
          value={
            calendar.description && calendar.description !== ''
              ? calendar.description
              : event.description
          }
          fullWidth
          variant="outlined"
          onChange={handleDescriptionChange}
        />

        <SubTitle>Providers</SubTitle>

        <CheckboxHolder>
          <Checkbox
            label="Google"
            onChange={(checked) => handleUpdateProviders('google', checked)}
            checked={isProviderChecked('google')}
          />
          <Checkbox
            label="Outlook"
            onChange={(checked) => handleUpdateProviders('outlook', checked)}
            checked={isProviderChecked('outlook')}
          />
          <Checkbox
            label="Yahoo"
            onChange={(checked) => handleUpdateProviders('yahoo', checked)}
            checked={isProviderChecked('yahoo')}
          />
          <Checkbox
            label="ICS / Apple"
            onChange={(checked) => handleUpdateProviders('apple', checked)}
            checked={isProviderChecked('apple')}
          />
        </CheckboxHolder>
        <SubTitle>Agenda</SubTitle>

        <Select
          onChange={onUnknownChangeHandler(handleEventTypeChange)}
          value={calendar.eventType || 'timed'}
        >
          <Option value="timed">Timed Event</Option>
          <Option value="multi">Multi Day Event</Option>
        </Select>

        <DayHolder>
          <DayInputs>
            {calendar.eventType !== 'multi' && (
              <>
                <LocalizedDateTimePicker
                  label="Start Date"
                  value={calendar.eventStart}
                  onChange={onChangeDate(handleCalendarEventStartChange)}
                  fullWidth
                  inputProps={{
                    'aria-label': 'start date',
                  }}
                />

                <LocalizedDateTimePicker
                  label="End Date"
                  value={calendar.eventEnd}
                  onChange={onChangeDate(handleCalendarEventEndChange)}
                  fullWidth
                  inputProps={{
                    'aria-label': 'end date',
                  }}
                />
              </>
            )}

            {calendar.eventType === 'multi' && (
              <>
                <LocalizedDatePicker
                  label="Start Date"
                  value={calendar.eventStart}
                  onChange={onChangeDate(handleCalendarEventStartChange)}
                  fullWidth
                  inputProps={{
                    'aria-label': 'start date',
                  }}
                />

                <LocalizedDatePicker
                  label="End Date"
                  value={calendar.eventEnd}
                  onChange={onChangeDate(handleCalendarEventEndChange)}
                  fullWidth
                  inputProps={{
                    'aria-label': 'end date',
                  }}
                />
              </>
            )}
          </DayInputs>
          <DayInputNote>
            Note: Calendar events will be in the users time zone, but settings
            on this page are based on the Event's time Zone which is set to{' '}
            {event?.timezone}.
          </DayInputNote>
        </DayHolder>

        <SubTitle>Preview</SubTitle>

        <CalendarStylesConfig
          styles={calendar.styles}
          onChange={handleButtonChange}
          widget={widget}
        />
      </Preview>

      <WidgetContainer>
        <WidgetTitle>
          <SubTitle>Widget Code</SubTitle>
          <CopyButton value={widgetCode} onCopy={regenerate} />
        </WidgetTitle>
        <CodeHolder>
          <code>{widgetCode}</code>
        </CodeHolder>
      </WidgetContainer>
    </>
  )
}

const DayInputs = styled.div`
  display: flex;
  gap: ${spacing[6]};
  align-items: center;

  & > div {
    flex: 1;
  }
`

const DayHolder = styled.div`
  border: 1px solid ${(props) => props.theme.colors.gray300};
  padding: ${spacing[4]} ${spacing[4]} 0 ${spacing[4]};
  margin-top: ${spacing[4]};
  margin-bottom: ${spacing[8]};
  border-radius: 4px;
  background: #e2e2e2;
`

const CheckboxHolder = styled.div`
  display: flex;
  gap: ${spacing[6]};
  margin-bottom: ${spacing[8]};
`

const DayInputNote = styled(Typography)`
  font-size: 14px;
  margin-bottom: ${spacing[2]};
`
